import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { modernTableContainer } from '../../styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#1F305E',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    fontSize: '17px',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#2a4073',
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '17px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

const SortButton = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '100%',
  minHeight: '40px',
});

const TitleCell = styled(Typography)({
  color: '#1F305E',
  cursor: 'pointer',
  textDecoration: 'underline',
});

const ListTable = ({
  rows = [],
  navigateToTrackingPage,
  //   orderHandler: _orderHandler,
  //   OrderSortBy = { columnId: 'ID', order: 'desc' },
  isDetailedListPage = false,
  showLeftPanel = true,
  showSearch = true,
}) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState([{ id: 'ID', desc: 'desc' }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFilter(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'ID',
        header: t('ID'),
        size: 80,
        sortingFn: 'basic',
        enableGlobalFilter: true,
        cell: ({ getValue, row }) => (
          <Typography
            width={50}
            sx={{
              cursor: 'pointer',
              fontWeight: 'bold',
              color: '#1F305E',
              textDecoration: 'underline',
            }}
            onClick={() => navigateToTrackingPage(row.original.ID)}
          >
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'allStatuses',
        header: t('Status'),
        size: 150,
        sortingFn: 'text',
        enableGlobalFilter: false,
        cell: ({ row }) => (
          <div className="block w-[200px]">
            {row?.original?.allStatuses.map((site, index) => (
              <div key={index} className="mb-1 text-left">
                <span className="font-bold" key={index}>
                  {`${site?.siteName}: `}
                </span>
                <span>{site?.status}</span>
              </div>
            ))}
          </div>
        ),
      },
      {
        accessorKey: 'Title',
        header: t('Title'),
        size: 200,
        sortingFn: 'text',
        enableGlobalFilter: true,
        cell: ({ getValue, row }) => (
          <Tooltip
            title={getValue() || ''}
            componentsProps={{
              tooltip: {
                sx: { fontSize: '14px' },
              },
            }}
          >
            <TitleCell
              noWrap
              width={200}
              onClick={() => navigateToTrackingPage(row.original.ID)}
            >
              {getValue()}
            </TitleCell>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'CreatedDate',
        header: t('Creation_Date'),
        size: 120,
        sortingFn: 'datetime',
        enableGlobalFilter: false,
        cell: ({ getValue }) => (
          <Typography width={80} sx={{ cursor: 'default' }}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'UserName',
        header: t('Site_Creator'),
        size: 150,
        sortingFn: 'text',
        enableGlobalFilter: false,
        cell: ({ getValue }) => (
          <Typography width={150} sx={{ cursor: 'default' }}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'Description',
        header: t('Description'),
        size: 300,
        sortingFn: 'text',
        enableGlobalFilter: false,
        cell: ({ getValue }) => (
          <Tooltip
            title={getValue() || ''}
            componentsProps={{
              tooltip: {
                sx: { fontSize: '14px' },
              },
            }}
          >
            <Typography noWrap width={300} sx={{ cursor: 'default' }}>
              {getValue()}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'ProductFamilyID',
        header: t('Product_Family'),
        size: 180,
        sortingFn: 'text',
        enableGlobalFilter: false,
        cell: ({ getValue }) => (
          <Typography width={180} sx={{ cursor: 'default' }}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'SitesImpacted',
        header: isDetailedListPage ? t('Plants_Impacted') : t('Sites_Impacted'),
        size: 120,
        sortingFn: 'basic',
        enableGlobalFilter: false,
        cell: ({ row }) => {
          const impacted = row?.original?.SitesImpacted;
          const sites = impacted ? impacted.split(',') : [];

          return (
            <div className="block w-[200px]">
              {sites.map((site, index) => (
                <div key={index}>{site}</div>
              ))}
            </div>
          );
        },
      },
    ];

    if (isDetailedListPage) {
      baseColumns.push(
        {
          accessorKey: 'Actor',
          header: t('Actors'),
          size: 250,
          sortingFn: 'text',
          enableGlobalFilter: false,
          cell: ({ getValue }) => (
            <Typography width={250} sx={{ cursor: 'default' }}>
              {getValue()}
            </Typography>
          ),
        },
        {
          accessorKey: 'OwnerName',
          header: t('Owners'),
          size: 250,
          sortingFn: 'text',
          enableGlobalFilter: false,
          cell: ({ getValue }) => (
            <Typography width={250} sx={{ cursor: 'default' }}>
              {getValue()}
            </Typography>
          ),
        },
        {
          accessorKey: 'LastModified',
          header: t('Last_Updated'),
          size: 200,
          sortingFn: 'datetime',
          enableGlobalFilter: false,
          cell: ({ getValue }) => (
            <Typography width={200} sx={{ cursor: 'default' }}>
              {getValue()}
            </Typography>
          ),
        },
        {
          accessorKey: 'Value',
          header: t('Value'),
          size: 130,
          sortingFn: 'basic',
          enableGlobalFilter: false,
          cell: ({ getValue }) => (
            <Typography width={130} sx={{ cursor: 'default' }}>
              {getValue()}
            </Typography>
          ),
        },
        {
          accessorKey: 'Effort',
          header: t('Effort'),
          size: 120,
          sortingFn: 'basic',
          enableGlobalFilter: false,
          cell: ({ getValue }) => (
            <Typography width={120} sx={{ cursor: 'default' }}>
              {getValue()}
            </Typography>
          ),
        }
      );
    }

    return baseColumns;
  }, [t, isDetailedListPage, navigateToTrackingPage]);

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: 100,
      },
    },
  });

  const handleSortingChange = header => {
    const currentSort = sorting.find(s => s.id === header.id);
    const newOrder = currentSort?.desc ? 'asc' : 'desc';

    setSorting([{ id: header.id, desc: newOrder === 'desc' }]);
  };

  const handleChangePage = (_, newPage) => {
    table.setPageIndex(newPage);
  };

  const handleChangeRowsPerPage = event => {
    table.setPageSize(parseInt(event.target.value, 10));
  };

  if (!rows.length) {
    return null;
  }

  return (
    <Box style={modernTableContainer}>
      <div>
        {showSearch && (
          <Box
            sx={{
              margin: '0.5rem 0',
              padding: '0px',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: '320px',
                backgroundColor: '#1F305E',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                padding: '0 0px 0 12px',
                '&:hover': {
                  borderColor: '#9ca3af',
                },
                '&:focus-within': {
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
              }}
            >
              <SearchIcon sx={{ color: 'white', marginRight: '6px' }} />
              <TextField
                placeholder={
                  t('Search by ID or Title') || 'Search by ID or Title...'
                }
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                variant="standard"
                sx={{
                  flex: 1,
                  '& .MuiInput-underline:before': {
                    display: 'none',
                  },
                  '& .MuiInput-underline:after': {
                    display: 'none',
                  },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    display: 'none',
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 6px 12px 6px',
                    fontSize: '15px',
                    color: '#374151',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </div>
      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <TableContainer
          sx={{
            height: 'calc(100vh - 350px)',
            maxWidth: isDetailedListPage && showLeftPanel ? '1520px' : '1840px',
          }}
        >
          <Table stickyHeader aria-label="PECR list table">
            <TableHead>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <StyledTableCell
                      key={header.id}
                      align="center"
                      onClick={() => handleSortingChange(header.column)}
                      sx={{ width: header.getSize() }}
                    >
                      <SortButton>
                        <span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                        {header.column.getIsSorted() ? (
                          header.column.getIsSorted() === 'desc' ? (
                            <ArrowDownwardIcon fontSize="small" />
                          ) : (
                            <ArrowUpwardIcon fontSize="small" />
                          )
                        ) : null}
                      </SortButton>
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <StyledTableRow key={row.id} hover>
                  {row.getVisibleCells().map(cell => (
                    <StyledTableCell key={cell.id} align="center">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={table.getFilteredRowModel().rows.length}
          rowsPerPage={table.getState().pagination.pageSize}
          page={table.getState().pagination.pageIndex}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={`${t('Rows_per_page')}:`}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${t('of')} ${count !== -1 ? count : `more than ${to}`}${
              globalFilter
                ? ` (${t('filtered from')} ${rows.length} ${t('total')})`
                : ''
            }`
          }
          sx={{
            boxShadow: 'inset 0 0 2px #000000',
            backgroundColor: '#1F305E',
            color: '#ffffff',
            '& .MuiTablePagination-actions button': {
              color: '#ffffff',
            },
            '& .MuiTablePagination-selectIcon': {
              color: '#ffffff',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

ListTable.propTypes = {
  rows: PropTypes.array.isRequired,
  navigateToTrackingPage: PropTypes.func.isRequired,
  orderHandler: PropTypes.func,
  OrderSortBy: PropTypes.shape({
    columnId: PropTypes.string,
    order: PropTypes.string,
  }),
  isDetailedListPage: PropTypes.bool,
  showLeftPanel: PropTypes.bool,
  showSearch: PropTypes.bool,
};

export default ListTable;
